import { useTranslation } from "next-i18next";
import { useMemo } from "react";
import { useProjectMapping } from "../../../data/projects/useAllProjects";
import { useProjectsForServiceDetail } from "../../../data/services/useProjectsForServiceDetail";
import { useServiceDetailMap } from "../../../data/sub-service/useServiceDetailMap";
import NWONLogoGrid from "../../01-atoms/NWONLogoGrid/NWONLogoGrid";
import { NWONSectionTitle } from "../../01-atoms/NWONSectionTitle/NWONSectionTitle";
import NWONCTAButton from "../../02-molecules/NWONCTAButton/NWONCTAButton";
import { ButtonLabel } from "../../02-molecules/NWONCTAButton/NWONCTAButtonInterfaces";
import { NWONKeyFacts } from "../../02-molecules/NWONKeyFacts/NWONKeyFacts";
import NWONCTASection from "../../03-organisms/NWONCTASection/NWONCTASection";
import { NWONFaq } from "../../03-organisms/NWONFaq/NWONFaq";
import NWONHeroSection from "../../03-organisms/NWONHeroSection/NWONHeroSection";
import { NWONProcessSteps } from "../../03-organisms/NWONProcessSteps/NWONProcessSteps";
import NWONProjectsSection from "../../03-organisms/NWONProjectsSection/NWONProjectsSection";
import NWONSection from "../../03-organisms/NWONSection/NWONSection";
import NWONServicesSection from "../../03-organisms/NWONServicesSection/NWONServicesSection";
import { NWONSubServicePageProps } from "./NWONSubServicePageInterfaces";
import { useNWONSubServicePageStyles } from "./NWONSubServicePageStyles";

const NWONSubServicePage = (props: NWONSubServicePageProps): JSX.Element => {
  const { classes } = useNWONSubServicePageStyles();
  const { service, serviceDetailPage } = props;
  const serviceDetailMap = useServiceDetailMap();
  const detailPage = serviceDetailMap[serviceDetailPage];
  const projectMap = useProjectMapping();
  const projectsForServiceDetail = useProjectsForServiceDetail();

  const projectData = useMemo(
    () =>
      projectsForServiceDetail[serviceDetailPage].map(
        (project) => projectMap[project]
      ),
    [projectMap, projectsForServiceDetail, serviceDetailPage]
  );

  const { t: tPages } = useTranslation<"pages">("pages");

  return (
    <>
      <NWONHeroSection
        title={detailPage.heroTitle}
        subtitle={detailPage.heroText}
        slideInformation={service.heroSlides}
        logos={service.heroLogos}
      />

      {detailPage.keyFacts && (
        <NWONSection>
          <NWONSectionTitle title={detailPage.pageTitle} />

          <NWONKeyFacts facts={detailPage.keyFacts} />

          <NWONCTAButton
            buttonVariant="outlined"
            buttonClass={classes.ctaButton}
            buttonLabel={ButtonLabel.Offer}
          />
        </NWONSection>
      )}

      {detailPage.processSteps && detailPage.processSteps.length > 0 && (
        <NWONSection>
          <NWONSectionTitle title={detailPage.processStepsTitle} />

          <NWONProcessSteps steps={detailPage.processSteps} />
        </NWONSection>
      )}

      {detailPage.toolLogos && detailPage.toolLogos.length > 0 && (
        <NWONSection>
          <NWONSectionTitle title={detailPage.toolLogosTitle} />

          <NWONLogoGrid logos={detailPage.toolLogos} />
        </NWONSection>
      )}

      <NWONCTASection
        teamMember={service.contactTeamMember}
        title={service.contactPersonHeader}
        subTitle={service.contactPersonSubTitle}
      />

      {detailPage.faq && detailPage.faq.length > 0 && (
        <NWONSection>
          <NWONSectionTitle title={detailPage.faqTitle} />

          <NWONFaq entries={detailPage.faq} />
        </NWONSection>
      )}

      <NWONProjectsSection
        title={detailPage.relatedProjectTitle}
        projects={projectData}
        allProjectsButton={false}
      />

      <NWONServicesSection title={tPages("service.otherServicesHeader")} />
    </>
  );
};

export default NWONSubServicePage;
